import { io } from 'socket.io-client';
import * as Sentry from '@sentry/react';
import debounce from 'lodash.debounce';
import { store } from '@@/store';
import { api } from '@@/services/api';
import { whatsappApi } from '@@/services/whatsapp';
import { ChatTypes, MESSAGES_PAGE_SIZE, Statuses } from '@@/constants/whatsapp';
import toast from 'react-hot-toast';

const RECONNECT_DELAY_MS = 2000;

class SocketIOManager {
  ws: any;
  hadDisconnect = false;

  constructor() {
    this.init();
  }

  init = () => {
    const token = store.getState().auth.accessToken;
    if (!token) {
      setTimeout(this.init, RECONNECT_DELAY_MS);
      return;
    }

    this.ws = io(import.meta.env.VITE_API_URL, {
      transports: ['websocket'],
      secure: true,
      auth: (cb: (data: { token: string }) => void) =>
        cb({ token: store.getState().auth.accessToken }),
    });

    this.ws.on('connect', () => {
      if (this.hadDisconnect) {
        this.hadDisconnect = false;
        // Socket.io drops inbound events while disconnected, so refetch every
        // tag that a socket handler can otherwise push to the cache.
        store.dispatch(
          api.util.invalidateTags([
            'Chats',
            'ChatMessages',
            'Tasks',
            'Documents',
            'Plans',
            'Suggestions',
            'Users',
          ]),
        );
      }
    });

    this.ws.on('disconnect', (reason: string) => {
      this.hadDisconnect = true;
      Sentry.addBreadcrumb({
        category: 'socket',
        level: 'info',
        message: 'socket disconnect',
        data: { reason },
      });
    });

    this.ws.on('connect_error', (err: { message?: string }) => {
      Sentry.addBreadcrumb({
        category: 'socket',
        level: 'warning',
        message: 'socket connect_error',
        data: { message: err?.message },
      });
    });

    this.registerEvents();

    this.ws.connect();
  };

  getWS = () => this.ws;

  // The conversation the user is currently viewing, registered explicitly by
  // ConversationPage: its `getChatMessages` cache key (the Chat `_id`) and
  // whether the message list is scrolled near the bottom. Used to scope the
  // safety-net refetch so a live event only ever refreshes the conversation on
  // screen, and never yanks the viewport while the user reads history.
  private activeConversationKey: string | null = null;
  private activeAtBottom = true;

  setActiveConversation = (chatKey: string | null, atBottom: boolean) => {
    this.activeConversationKey = chatKey;
    this.activeAtBottom = atBottom;
  };

  // Guaranteed-render safety net: when a live event can't be placed optimistically
  // (the message landed under a different/unlinked chat than the one on screen, or
  // arrived before the first page loaded), force-refetch the active conversation.
  // `forceSync` makes the API run a wweb gap-detect so a just-arrived message is
  // pulled into the canonical chat. Debounced (1s) to coalesce inbound bursts and
  // bound the puppeteer gap-detect load when messages stream in for other chats.
  private refetchActiveConversation = debounce(() => {
    const key = this.activeConversationKey;
    if (!key) {
      return;
    }
    store.dispatch(
      whatsappApi.endpoints.getChatMessages.initiate(
        { chatId: key, limit: MESSAGES_PAGE_SIZE, forceSync: true },
        { subscribe: false, forceRefetch: true },
      ),
    );
  }, 1000);

  // When a live event's optimistic cache patch didn't land (`applied` false) and
  // a conversation is on screen scrolled near the bottom, schedule the safety-net
  // refetch so the message still surfaces without disturbing the user's scroll.
  private maybeFallbackRefetch = (applied: boolean) => {
    if (!applied && this.activeConversationKey && this.activeAtBottom) {
      this.refetchActiveConversation();
    }
  };

  private registerEvents = () => {
    if (!this.ws) {
      return;
    }

    this.ws.on('qr', () => {
      store.dispatch(api.util.invalidateTags(['Chats']));
    });

    this.ws.on('status', (s: Statuses) => {
      if (s === Statuses.READY) {
        store.dispatch(api.util.invalidateTags(['Chats']));
      }
    });

    this.ws.on('msg', (msg: Unpatient.ChatMessage) => {
      // Match by the immutable Chat `_id` the server stamps on every emit, so
      // live delivery never depends on the volatile `@lid`/`@c.us` serialized.
      const chatId = msg.chatId ?? msg.id.remote;
      // `updateQueryData`'s recipe only runs when a cached `getChatMessages`
      // entry exists for `chatId`. Track that so we can fall back to a refetch
      // when it doesn't (otherwise the insert silently no-ops).
      let applied = false;

      if (chatId) {
        store.dispatch(
          whatsappApi.util.updateQueryData(
            'getChatMessages',
            { chatId },
            (draft) => {
              applied = true;
              if (draft.find((m) => m.id.id === msg.id.id)) {
                return;
              }
              // Binary-search insert by (timestamp, _id) so WhatsApp's
              // out-of-order live deliveries (multi-device sync, gap-fill,
              // post-reconnect catch-up) land in chronological order
              // instead of always appearing at the bottom.
              const ts = msg.timestamp ?? 0;
              const id = msg._id ?? '';
              let lo = 0;
              let hi = draft.length;
              while (lo < hi) {
                const mid = (lo + hi) >>> 1;
                const m = draft[mid];
                const mts = m.timestamp ?? 0;
                if (mts < ts || (mts === ts && (m._id ?? '') < id)) {
                  lo = mid + 1;
                } else {
                  hi = mid;
                }
              }
              draft.splice(lo, 0, msg);
            },
          ),
        );
      }

      this.maybeFallbackRefetch(applied);

      setTimeout(() => {
        // API needs a delay to be up to date
        store.dispatch(api.util.invalidateTags(['Chats']));
      }, 1000);
    });

    this.ws.on(
      'msg-media',
      ({
        chatId,
        messageId,
        documentId,
      }: {
        chatId: string;
        messageId: string;
        documentId: string;
      }) => {
        if (!chatId || !messageId) return;
        store.dispatch(
          whatsappApi.util.updateQueryData(
            'getChatMessages',
            { chatId },
            (draft) => {
              const m = draft.find((mm) => mm.id.id === messageId);
              if (m) {
                m._data = { ...(m._data || {}), documentId };
              }
            },
          ),
        );
      },
    );

    this.ws.on(
      'msg-revoke',
      ({
        chatId,
        messageId,
      }: {
        chatId: string;
        messageId: string;
        scope: 'everyone' | 'me';
      }) => {
        if (!chatId || !messageId) return;
        let applied = false;
        store.dispatch(
          whatsappApi.util.updateQueryData(
            'getChatMessages',
            { chatId },
            (draft) => {
              applied = true;
              const m = draft.find((mm) => mm.id.id === messageId);
              if (m) {
                // Surface WhatsApp's "deleted" state live: Message.tsx renders
                // "Message supprimé" and ConversationPage hides the edit/delete
                // controls when type === 'revoked'.
                m.type = ChatTypes.REVOKED;
                m._data = { ...(m._data || {}), isRevoked: true };
              }
            },
          ),
        );
        this.maybeFallbackRefetch(applied);
        // Refresh the conversation list so a revoked last message shows as
        // deleted in the sidebar preview (mirrors the `msg` handler). The API
        // leaves Chat.lastMessageId pointing at the same row, which now adapts
        // to type 'revoked'.
        store.dispatch(api.util.invalidateTags(['Chats']));
      },
    );

    this.ws.on(
      'msg-edit',
      ({
        chatId,
        messageId,
        body,
        editedAt,
      }: {
        chatId: string;
        messageId: string;
        body: string;
        editedAt?: string;
      }) => {
        if (!chatId || !messageId) return;
        let applied = false;
        store.dispatch(
          whatsappApi.util.updateQueryData(
            'getChatMessages',
            { chatId },
            (draft) => {
              applied = true;
              const m = draft.find((mm) => mm.id.id === messageId);
              if (m) {
                // Surface WhatsApp's in-place edit live: Message.tsx renders
                // `body` for chat messages. Keep `_data.editedAt` in sync so a
                // later refetch doesn't flicker the message back.
                m.body = body;
                m._data = { ...(m._data || {}), editedAt };
              }
            },
          ),
        );
        this.maybeFallbackRefetch(applied);
        // Refresh the conversation list so an edited last message updates the
        // sidebar preview (mirrors the `msg` and `msg-revoke` handlers).
        store.dispatch(api.util.invalidateTags(['Chats']));
      },
    );

    this.ws.on('update-medical-record-started', () => {
      toast.loading(`Dossier médical en cours de mise à jour...`, {
        position: 'top-center',
        duration: 5000,
      });
    });

    this.ws.on('user', (user: Unpatient.User) => {
      store.dispatch(api.util.invalidateTags(['Users']));
      toast.success(
        `Le dossier médical de ${user.firstName} ${user.lastName} a été mis à jour !`,
        {
          position: 'top-right',
        },
      );
    });

    this.ws.on('create-plan-started', () => {
      toast.loading(`Un nouveau plan de soins est en cours de conception...`, {
        position: 'top-center',
        duration: 5000,
      });
    });

    this.ws.on('plan', (plan: Unpatient.Plan) => {
      store.dispatch(api.util.invalidateTags(['Plans']));
      toast.success(`Un nouveau plan de soins est disponible !`, {
        position: 'top-right',
      });
    });

    this.ws.on('create-suggestion-started', () => {
      toast.loading(`Une nouvelle suggestion est en cours de conception...`, {
        position: 'top-center',
        duration: 1000,
      });
    });

    this.ws.on('suggestion', (suggestion: Unpatient.Suggestion) => {
      store.dispatch(api.util.invalidateTags(['Suggestions']));
      toast.success(`Une nouvelle suggestion est disponible !`, {
        position: 'top-right',
      });
    });

    this.ws.on('extract-tasks-started', () => {
      toast.loading(`Extraction de tâches en cours...`, {
        position: 'top-center',
        duration: 1000,
      });
    });

    this.ws.on('refresh-tasks', () => {
      store.dispatch(api.util.invalidateTags(['Tasks']));
    });

    this.ws.on('refresh-patient-documents', () => {
      store.dispatch(api.util.invalidateTags(['Documents']));
    });

    this.ws.on('new-patient', (patient: Unpatient.User) => {
      toast.success(
        `La patient ${patient.firstName} ${patient.lastName} a été créé`,
        {
          position: 'top-right',
        },
      );
    });
  };
}

export default new SocketIOManager();
